import {Canvas} from '@react-three/fiber'
import {OrbitControls} from '@react-three/drei'
import * as THREE from 'three'
import {useEffect, useRef, useState} from "react";
import "./App.css";
import {CP, initializate, initParam, onReady} from "./setting/initParam";
import {getPillar} from "./elements/getPillar";
import {getHarness} from "./elements/getHarness";
import {getF1} from "./elements/getF1";
import {getRafter} from "./elements/getRafter";
import {getF2} from "./elements/getF2";
import {getRidge} from "./elements/getRidge";
import {getF3} from "./elements/getF3";
import {getF4} from "./elements/getF4";
import {getF5} from "./elements/getF5";
import {getF6} from "./elements/getF6";
import {getF7} from "./elements/getF7";
import {getPK} from "./elements/getPK";
import {getSheathing} from "./elements/getSheathing";
import {MeshStandardMaterial} from "three";
import {
    ColorPicker,
    createSlotZonesBetweenPillar,
    DimensionLine,
    DockAction,
    DockBrand,
    FillerPicker,
    fromRenderStructDTOArray,
    Ground,
    hexToDecimal,
    IconCalc,
    IconSchema,
    InAppBrowserBanner,
    initAnalytics,
    InitialStruct,
    LeadButton,
    LeadForm,
    LeadFormData,
    LeadStack,
    LightV1,
    ModalPage,
    ParamDock,
    ParamsControl,
    Question,
    RenderLayout,
    RenderStruct,
    Slots,
    SlotZone,
    SpecStructElement,
    StructData,
    useAnalytics,
    useCameraSettings,
    useEngagementPulse,
    useParamsTracking,
} from "@3dcalc/3dpClient";
import {api} from "./api/api";

function roundTo(num: number, decimals: number) {
    const factor = 10 ** decimals;
    return Math.round(num * factor) / factor;
}

const DEFAULT_COLORS = [
    '#8c4d24', '#a0522d', '#6b3a2a', '#c8a96e',
    '#d2691e', '#4a3728', '#d4b896', '#8b6914',
    '#2c2c2c', '#f5f5f0'
]

initAnalytics({
    ymCounterId: 108350472,
    debug: false,
});

export default function App() {

    const modeBuild = true

    const colorMapRef = useRef<Record<string, string>>({})
    const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [activeSlot, setActiveSlot] = useState<Slots | null>(null)

    const [fillerStructs, setFillerStructs] = useState<Record<string, RenderStruct[]>>({});
    const [slots, setSlots] = useState<Slots[]>([]);
    const [palette, setPalette] = useState<string[]>(DEFAULT_COLORS)

    const modelRef = useRef<THREE.Group | null>(null)
    const [dataRender, setDataRender] = useState<RenderStruct[]>([])
    const [dataSpec, setDataSpec] = useState<SpecStructElement[]>([])
    const [dataSpecFiller, setDataSpecFiller] = useState<Record<string, SpecStructElement[]>>({})

    const [debugMode, setDebugMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<RenderStruct>()
    const [selectedIndex, setSelectedIndex] = useState<number>(-1)
    const [selectedSource, setSelectedSource] = useState<'main' | string>('main')
    const [currentColor, setCurrentColor] = useState<string>()

    const { trackEvent } = useAnalytics();
    const { pulse, notify, reset } = useEngagementPulse({
        timeMs: 15_000,
        interactions: 3,
        pulseDuration: 8_000,
    })

    const [isReady, setIsReady] = useState(false)

    useEffect(() => { onReady(() => setIsReady(true)) }, [])

    const [dimensions, setDimensions] = useState({
        height: initParam.sizes.h,
        width: initParam.sizes.w,
        length: initParam.sizes.l,
        angle: initParam.a,
        roofExtension: {
            left: initParam.e.left,
            right: initParam.e.right,
            front: initParam.e.front,
            back: initParam.e.back
        }
    })

    useEffect(() => {
        onReady(() => {
            setDimensions({
                height: initParam.sizes.h,
                width: initParam.sizes.w,
                length: initParam.sizes.l,
                angle: initParam.a,
                roofExtension: { ...initParam.e },
            })
        })
    }, [])

    const [params, setParams] = useState<InitialStruct>({
        h: initParam.sizes.h,
        l: initParam.sizes.l,
        w: initParam.sizes.w,
        a: initParam.a,
        r: initParam.a * Math.PI / 180,
        e: dimensions.roofExtension,
    })

    const cameraSettings = useCameraSettings()

    useParamsTracking(params, {
        debounceMs: 2000,
        transform: ({ r, ...rest }) => rest as any,
    });

    useEffect(() => {
        setParams({
            h: dimensions.height,
            w: dimensions.width,
            l: dimensions.length,
            a: dimensions.angle,
            r: dimensions.angle * Math.PI / 180,
            e: dimensions.roofExtension,
        })
    }, [dimensions])

    useEffect(() => {
        if (!isReady) return
        if (resizeTimerRef.current) {
            clearTimeout(resizeTimerRef.current);
        }
        setIsCalculating(true);

        resizeTimerRef.current = setTimeout(() => {
            resize();
        }, 2000);

        return () => {
            if (resizeTimerRef.current) {
                clearTimeout(resizeTimerRef.current);
            }
        };
    }, [params, isReady]);

    const resizePillar = async (): Promise<StructData> => {
        const sizePillar = structuredClone(initParam.elementData.pillar.sizeBoard)
        sizePillar.l = params.h
        return await getPillar(params, sizePillar, modeBuild);
    }

    const resizeRafter = async (): Promise<StructData> => {
        const sizeRafter = structuredClone(initParam.elementData.rafter.sizeBoard)
        return await getRafter(params, sizeRafter, modeBuild);
    }

    const resizeF2 = async (): Promise<StructData> => {
        const sizeF2 = structuredClone(initParam.elementData.f2.sizeBoard)
        return await getF2(params, sizeF2, modeBuild);
    }

    const resizeRidge = async (): Promise<StructData> => {
        const sizeRidge = structuredClone(initParam.elementData.ridge.sizeBoard)
        sizeRidge.l = params.l
        return await getRidge(params, sizeRidge, modeBuild);
    }

    const resizeF3 = async (): Promise<StructData> => {
        const sizeF3 = structuredClone(initParam.elementData.f3.sizeBoard)
        sizeF3.l = 0
        return await getF3(params, sizeF3, modeBuild);
    }

    const resizeF4 = async (): Promise<StructData> => {
        const sizeF4 = structuredClone(initParam.elementData.f4.sizeBoard)
        sizeF4.l = 0
        return await getF4(params, sizeF4, modeBuild);
    }

    const resizeF5 = async (): Promise<StructData> => {
        const sizeF5 = structuredClone(initParam.elementData.f5.sizeBoard)
        sizeF5.l = CP.maps.f4.point[1] + initParam.gash.simple
        return await getF5(params, sizeF5, modeBuild);
    }

    const resizeF6 = async (): Promise<StructData> => {
        const sizeF6 = structuredClone(initParam.elementData.f6.sizeBoard)
        sizeF6.l = 0
        return await getF6(params, sizeF6, modeBuild);
    }

    const resizeF7 = async (): Promise<StructData> => {
        const sizeF7 = structuredClone(initParam.elementData.f7.sizeBoard)
        sizeF7.l = CP.maps.f6.point[1] + initParam.gash.simple
        return await getF7(params, sizeF7, modeBuild);
    }

    const resizeF1 = async (): Promise<StructData> => {
        const sizeF1 = structuredClone(initParam.elementData.f1.sizeBoard)
        sizeF1.l = params.w - initParam.elementData.pillar.sizeBoard.h
        return await getF1(params, sizeF1, modeBuild);
    }

    const resizeHarness = async (): Promise<StructData> => {
        const sizeHarness = structuredClone(initParam.elementData.harness.sizeBoard)
        sizeHarness.l = params.l
        return await getHarness(params, sizeHarness, modeBuild);
    }

    const resizeSheathing = async (): Promise<StructData> => {
        const sizeSheathing = structuredClone(initParam.elementData.sheathing.sizeBoard)
        sizeSheathing.l = 0
        return await getSheathing(params, sizeSheathing, modeBuild);
    }

    const resizePK = async (): Promise<StructData> => {
        const sizePK = structuredClone(initParam.elementData.pk.sizeBoard)
        sizePK.l = 0
        return await getPK(params, sizePK, modeBuild);
    }

    const applyColors = (dataRender: RenderStruct[]): RenderStruct[] => {
        return dataRender.map(item => {
            const savedColor = colorMapRef.current[item.group]
            if (!savedColor) return item

            return {
                ...item,
                material: item.material.map(mat => {
                    const m = (mat as THREE.MeshStandardMaterial).clone()
                    m.color.set(savedColor)
                    return m
                }) as THREE.Material[]
            }
        })
    }

    const createSlotZone = () => {
        const _slots = createSlotZonesBetweenPillar({
            id: "wall",
            maps: CP.maps.pillar.lineCentered,
            sizeBoard: {
                h: initParam.elementData.pillar.sizeBoard.h,
                l: params.h - initParam.gash.simple,
                w: initParam.elementData.pillar.sizeBoard.w
            },
            sizes: {h: params.h, l: params.l, w: params.w}
        })
        setSlots(_slots)
        setFillerStructs({});
    }

    const resize = async () => {
        setModalOpen(false)
        const _dataRender: RenderStruct[] = []
        const _specData: SpecStructElement[] = []
        initializate()

        let t = await resizePillar()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeRafter()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeF2()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeRidge()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeF3()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)

        if (params.w > 3.5) {
            t = await resizeF4()
            _dataRender.push(t.rs)
            _specData.push(...t.spec)
        }

        if (params.w > 6) {
            t = await resizeF5()
            _dataRender.push(t.rs)
            _specData.push(...t.spec)

            if (params.w > 7) {
                t = await resizeF6()
                _dataRender.push(t.rs)
                _specData.push(...t.spec)
                t = await resizeF7()
                _dataRender.push(t.rs)
                _specData.push(...t.spec)
            }
        }

        t = await resizeF1()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeHarness()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeSheathing()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizePK()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)

        const restored = applyColors(_dataRender)

        setDataRender(restored)
        setDataSpec(_specData)
        setIsCalculating(false)

        createSlotZone()
    }

    const updateDimension = (
        field: 'height' | 'width' | 'length' | 'angle',
        value: number
    ) => {
        const rounded = roundTo(value, 2)
        const limits = {
            height: { min: initParam.minSize.h, max: initParam.maxSize.h },
            width: { min: initParam.minSize.w, max: initParam.maxSize.w },
            length: { min: initParam.minSize.l, max: initParam.maxSize.l },
            angle: { min: initParam.minSize.a, max: initParam.maxSize.a },
        }

        const { min, max } = limits[field]
        const validated = Math.max(min, Math.min(max, rounded))

        setDimensions(prev => ({ ...prev, [field]: validated }))
        notify()
    }

    const updateRoofExtension = (
        side: 'left' | 'right' | 'front' | 'back',
        delta: number,
        directValue?: number
    ) => {
        const limits = {
            left: { min: initParam.minSize.rel, max: initParam.maxSize.rel },
            right: { min: initParam.minSize.rer, max: initParam.maxSize.rer },
            front: { min: initParam.minSize.ref, max: initParam.maxSize.ref },
            back: { min: initParam.minSize.reb, max: initParam.maxSize.reb },
        }

        setDimensions(prev => {
            const currentValue = prev.roofExtension[side]
            const newValue = directValue !== undefined
                ? roundTo(directValue, 2)
                : roundTo(currentValue + delta, 2)

            const { min, max } = limits[side]
            const validated = Math.max(min, Math.min(max, newValue))

            return {
                ...prev,
                roofExtension: {
                    ...prev.roofExtension,
                    [side]: validated
                }
            }
        })
        notify()
    }

    const saveCalculation = async () => {
        trackEvent("lead_form_open");
        setShowForm(true)
    }

    const handleFormClose = async () => {
        trackEvent("lead_form_close");
        setShowForm(false)
    }

    const questions: Question[] = [
        {
            id: 'service',
            type: 'checkbox',
            label: 'Дополнительные услуги',
            options: ['Свайный фундамент', 'Доставка', 'Монтаж', 'Водосточная система'],
            required: false,
        },
        {
            id: 'delivery_address',
            type: 'text',
            label: 'Адрес доставки',
            placeholder: 'г. Москва, ул. Примерная, д. 1',
            required: false,
        },
    ];

    const sendToPlanfix = async ({
                                     name,
                                     phone,
                                     answers,
                                     link,
                                 }: {
        name: string;
        phone: string;
        answers: Record<string, string | string[]>;
        link: string;
    }) => {
        const payload = {
            name,
            phone,
            services: (answers.service as string[])?.join(', ') ?? '',
            delivery_address: (answers.delivery_address as string) ?? '',
            calculation_link: link,
        };

        await fetch('https://lh.planfix.ru/webhook/json/ufhu-dd78-068x-3rcp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
    };

    const handleLeadSubmit = async ({ name, phone, answers }: LeadFormData) => {
        const information: Record<string, any>[] = [
            { label: 'Длина',                value: `${dimensions.length.toFixed(2)} м` },
            { label: 'Ширина',               value: `${dimensions.width.toFixed(2)} м` },
            { label: 'Высота',               value: `${dimensions.height.toFixed(2)} м` },
            { label: 'Угол фермы',           value: `${dimensions.angle.toFixed(1)}°` },
            { label: 'Вынос крыши слева',    value: `${dimensions.roofExtension.left.toFixed(2)} м` },
            { label: 'Вынос крыши справа',   value: `${dimensions.roofExtension.right.toFixed(2)} м` },
            { label: 'Вынос крыши спереди',  value: `${dimensions.roofExtension.front.toFixed(2)} м` },
            { label: 'Вынос крыши сзади',    value: `${dimensions.roofExtension.back.toFixed(2)} м` },
        ]

        const res = await api.calculationSave.build({
            data: [...dataRender, ...Object.values(fillerStructs).flat()],
            visibility: "link_only",
            params: initParam,
            spec: [...dataSpec, ...Object.values(dataSpecFiller).flat()],
            // leads: { name, phone, ...answers },
            information,
        })
        const link = `https://deal3dp.ru/viewer/${res.id}`

        trackEvent("configurator_lead_submit", {
            has_name: !!name,
            has_phone: !!phone,
        });

        await sendToPlanfix({ name, phone, answers, link });
    }

    const handleFillerSelect = async (slotId: string, fillerId: string) => {
        const slot = slots.find(s => s.id === slotId);
        if (!slot) return;
        const result = await api.fillers.calculate({
            fillerId,
            width: slot.width,
            height: slot.height,
        });
        const renderStruct = fromRenderStructDTOArray(result.props.render.rs);
        const withPosition = renderStruct.map(s => ({
            ...s,
            position: slot.position,
            rotation: slot.rotation,
        }))

        setFillerStructs(prev => ({ ...prev, [slotId]: withPosition }));
        if (result.props.render?.spec && result.props.render?.spec.length > 0) {
            setDataSpecFiller(prev => ({ ...prev, [slotId]: result.props.render.spec as SpecStructElement[] }))
        }
        trackEvent("filler_selected");
        notify()
    };

    const controls = [
        { label: 'Длина',               value: dimensions.length,              step: 0.10, onChange: (v: number) => updateDimension('length', v) },
        { label: 'Ширина',              value: dimensions.width,               step: 0.10, onChange: (v: number) => updateDimension('width', v) },
        { label: 'Высота',              value: dimensions.height,              step: 0.10, onChange: (v: number) => updateDimension('height', v) },
        { label: 'Угол фермы',          value: dimensions.angle,               step: 0.5,  isAngle: true, onChange: (v: number) => updateDimension('angle', v) },
        { label: 'Вынос крыши слева',   value: dimensions.roofExtension.left,  step: 0.05, isOverhang: true, onChange: (v: number) => updateRoofExtension('left', 0, v) },
        { label: 'Вынос крыши справа',  value: dimensions.roofExtension.right, step: 0.05, isOverhang: true, onChange: (v: number) => updateRoofExtension('right', 0, v) },
        { label: 'Вынос крыши спереди', value: dimensions.roofExtension.front, step: 0.05, isOverhang: true, onChange: (v: number) => updateRoofExtension('front', 0, v) },
        { label: 'Вынос крыши сзади',   value: dimensions.roofExtension.back,  step: 0.05, isOverhang: true, onChange: (v: number) => updateRoofExtension('back', 0, v) },
    ]

    return (
        <div className={"page"} style={{ height: '100dvh', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <InAppBrowserBanner />

            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div className={"visual-wrapper"} style={{ position: 'relative', width: '100%', margin: '0 auto', display: 'flex' }}>
                    {isCalculating && (
                        <div className="calculating-overlay-canvas">
                            <div className="spinner"></div>
                            <div>Расчёт конструкции...</div>
                        </div>
                    )}
                    <div className="canvas-wrapper" style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
                        <Canvas
                            key={`${cameraSettings.fov}`}
                            camera={{ position: cameraSettings.position, fov: cameraSettings.fov }}
                            style={{ width: '100%', height: '100%', margin: '0 auto' }}
                        >
                            <Ground />

                            {debugMode ? <>
                                <DimensionLine from={[params.l / 2, 0, -params.w / 2]} to={[params.l / 2, 0, params.w / 2]} />
                                <DimensionLine from={[-params.l / 2, 0, params.w / 2]} to={[params.l / 2, 0, params.w / 2]} />
                                <DimensionLine from={[params.l / 2, params.h - initParam.gash.simple, params.w / 2]} to={[params.l / 2, 0, params.w / 2]} />
                            </> : null}

                            <LightV1 />

                            {slots.map(slot => (
                                <SlotZone
                                    key={slot.id}
                                    id={slot.id}
                                    position={slot.position}
                                    rotation={slot.rotation}
                                    width={slot.width}
                                    height={slot.height}
                                    onOpen={(id) => setActiveSlot(slots.find(s => s.id === id) ?? null)}
                                />
                            ))}

                            <group ref={modelRef} name={'full'}>
                                <RenderLayout
                                    struct={dataRender}
                                    palette={palette}
                                    onPaletteChange={setPalette}
                                    isEditor={true}
                                    onColorChange={()=>{}}
                                    onGroupClick={(groupIndex, groupData) => {
                                        setSelectedIndex(groupIndex)
                                        setSelectedGroup(groupData)
                                        setSelectedSource('main')
                                        setCurrentColor(colorMapRef.current[groupData.group] ?? '#8c4d24')
                                        setModalOpen(true)
                                    }}
                                />
                                {slots.map(slot => {
                                    const struct = fillerStructs[slot.id];
                                    if (!struct) return null;
                                    return (
                                        <RenderLayout
                                            key={slot.id}
                                            struct={struct}
                                            palette={palette}
                                            onPaletteChange={setPalette}
                                            isEditor={true}
                                            onColorChange={()=>{}}
                                            onGroupClick={(groupIndex, groupData) => {
                                                setSelectedIndex(groupIndex)
                                                setSelectedGroup(groupData)
                                                setSelectedSource(slot.id)
                                                setCurrentColor(colorMapRef.current[groupData.group] ?? '#8c4d24')
                                                setModalOpen(true)
                                            }}
                                        />
                                    );
                                })}
                            </group>

                            <OrbitControls />
                        </Canvas>
                    </div>

                    <ParamDock>
                        <ParamsControl
                            controls={controls}
                            units="m"
                            label={'Параметры'}
                            lang={'ru'}
                        />
                        <DockAction
                            icon={<IconSchema />}
                            title="Схема"
                            active={debugMode}
                            onClick={() => setDebugMode(d => !d)}
                        />
                        <DockBrand href="https://deal3dp.ru" />
                    </ParamDock>

                    <LeadStack pulse={pulse}>
                        <LeadButton
                            label="Рассчитать стоимость"
                            subtitle="Пришлём расчёт"
                            icon={<IconCalc />}
                            variant="primary"
                            onClick={() => {
                                reset()
                                saveCalculation()
                            }}
                        />
                    </LeadStack>

                    {activeSlot && (
                        <div style={{
                            position: 'fixed', left: 0, top: 0, bottom: 0,
                            width: window.innerWidth < 768 ? '100vw' : '50vw',
                            background: 'rgba(0,0,0,0.58)', zIndex: 1000,
                            boxShadow: '8px 0 32px rgba(0,0,0,0.15)',
                            overflowY: 'auto', padding: 24,
                        }}>
                            <FillerPicker
                                width={activeSlot.width}
                                height={activeSlot.height}
                                depth={activeSlot.depth}
                                slotType={activeSlot.type ?? 'wall'}
                                fetchAvailable={api.fillers.available}
                                onSelect={(fillerId) => {
                                    handleFillerSelect(activeSlot.id, fillerId)
                                    setActiveSlot(null)
                                }}
                                onClose={() => setActiveSlot(null)}
                            />
                        </div>
                    )}
                </div>
            </div>

            {showForm && (
                <LeadForm
                    onSubmit={handleLeadSubmit}
                    onClose={handleFormClose}
                    title="Сохранить конфигурацию"
                    subtitle="Укажите контакты и мы пришлём расчёт"
                    questions={questions}
                    lang={"ru"}
                    country={'ru'}
                />
            )}

            <ModalPage
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title={selectedGroup?.group}
                groupDataList={
                    selectedSource === 'main'
                        ? dataRender.filter(r => r.group === selectedGroup?.group)
                        : (fillerStructs[selectedSource] ?? []).filter(r => r.group === selectedGroup?.group)
                }
                spec={
                    selectedSource === 'main'
                        ? dataSpec.filter(s => s.group === selectedGroup?.group)
                        : (dataSpecFiller[selectedSource] ?? []).filter(s => s.group === selectedGroup?.group)
                }
                lang="ru"
                units={"m"}
            >
                <ColorPicker
                    value={currentColor}
                    onChange={hex => {
                        setCurrentColor(hex)
                        if (selectedIndex < 0) return
                        if (selectedSource === 'main') {
                            const groupName = selectedGroup?.group?.toLowerCase()
                            if (groupName && initParam.elementData[groupName]?.color !== undefined) {
                                initParam.elementData[groupName].color = hexToDecimal(hex)
                            }
                            colorMapRef.current[selectedGroup!.group as string] = hex
                            setDataRender(prev => prev.map((item, idx) => {
                                if (idx !== selectedIndex) return item
                                const updated = {
                                    ...item,
                                    material: item.material.map(mat => {
                                        const m = (mat as MeshStandardMaterial).clone()
                                        m.color.set(hex)
                                        return m as THREE.Material
                                    })
                                }
                                setSelectedGroup(updated)
                                return updated
                            }))
                        } else {
                            setFillerStructs(prev => ({
                                ...prev,
                                [selectedSource]: prev[selectedSource].map((item, idx) => {
                                    if (idx !== selectedIndex) return item
                                    const updated = {
                                        ...item,
                                        material: item.material.map(mat => {
                                            const m = (mat as MeshStandardMaterial).clone()
                                            m.color.set(hex)
                                            return m as THREE.Material
                                        })
                                    }
                                    setSelectedGroup(updated)
                                    return updated
                                })
                            }))
                        }
                    }}
                    palette={palette}
                    onPaletteChange={setPalette}
                    lang="ru"
                />
            </ModalPage>
        </div>
    )
}
